<script setup lang="ts">
/**
 * The `role="listbox"` panel. Portalled to `<body>` and positioned by reka's popper against the
 * anchor, at the anchor's width, flipping and shifting to stay in the viewport — the same job
 * `ui/dropdown-menu`'s content does. Focus stays in the input; the arrow keys move the highlight.
 * `hide-when-empty` hides it while there is no option to show (or use `ComboboxEmpty` to say so).
 */
import type { ComboboxContentEmits, ComboboxContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import {
  ComboboxContent,
  ComboboxPortal,
  ComboboxViewport,
  useForwardPropsEmits,
} from "reka-ui"
import { cn } from "@/lib/utils"

// The root element is the portal, so attributes (`data-testid`, `aria-label`, …) are bound onto the
// panel explicitly instead of falling through to nowhere.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<ComboboxContentProps & { class?: HTMLAttributes["class"] }>(),
  { position: "popper", align: "start", sideOffset: 4, collisionPadding: 8 },
)
const emits = defineEmits<ComboboxContentEmits>()

const delegatedProps = reactiveOmit(props, "class")

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <ComboboxPortal>
    <ComboboxContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="cn(
        'z-50 w-[var(--reka-combobox-trigger-width)] max-h-[min(16rem,var(--reka-combobox-content-available-height))] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none',
        props.class,
      )"
    >
      <ComboboxViewport class="overflow-y-auto py-1">
        <slot />
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxPortal>
</template>
